/**
 * File: PasswordMatchField.js
 * Origin: isotect
 * Description: PasswordMatchField component for authentication forms.
 *
 * Version: v1.0.1
 * Created: 2025-07-27
 * Last Updated: 2026-04-10
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useState } from "react";

// Local project files
import IconLabel from "../ui/IconLabel";
import PasswordField from "./PasswordField";

// Icons
import { FaCheck } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";

/**
 * PasswordMatchHint component displays a hint under a password confirmation field
 * indicating whether the confirmed password matches the original password.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {boolean} props.isPasswordMatch - Whether the confirmed password matches the original password
 *
 * @returns {JSX.Element} A visual hint showing password match status
 *
 * @example
 * <PasswordMatchHint isPasswordMatch={true} />
 */
function PasswordMatchHint({ isPasswordMatch }) {
    return (
        <>
            <div>
                {isPasswordMatch ? (
                    <IconLabel icon={<FaCheck />} text={"Passwords Match"} />
                ) : (
                    <IconLabel
                        icon={<FaXmark />}
                        text={"Passwords Don't Match"}
                    />
                )}
            </div>
        </>
    );
}

/**
 * PasswordMatchField component renders a password input field for confirming
 * that the entered password matches the original password.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {string} props.label - Label text for the input field
 * @param {string} props.id - Unique ID for the input field
 * @param {string} [props.placeholder] - Placeholder text for the input field
 * @param {string} props.newPassword - Original password value to match against
 *
 * @returns {JSX.Element} A password input field with validation hints for matching the original password
 *
 * @example
 * <PasswordMatchField
 *   label="Confirm Password"
 *   id="confirm-password"
 *   placeholder="Re-enter your password"
 *   newPassword={password}
 * />
 */
function PasswordMatchField({ label, id, placeholder, newPassword }) {
    const [password, setPassword] = useState("");

    let isPasswordMatch = password === newPassword;

    return (
        <>
            <PasswordField
                label={label}
                id={id}
                placeholder={placeholder}
                password={password}
                setPassword={setPassword}
                isValidationHint={false}
            />
            {newPassword.length > 0 || password.length > 0
                ? PasswordMatchHint({ isPasswordMatch })
                : null}
        </>
    );
}

export default PasswordMatchField;
